import { useEffect, useRef } from 'react';

type WakeLockType = 'screen';

interface WakeLockSentinelLike {
    readonly released?: boolean;
    release: () => Promise<void>;
    addEventListener?: (type: 'release', listener: () => void) => void;
    removeEventListener?: (type: 'release', listener: () => void) => void;
}

interface WakeLockApiLike {
    request: (type: WakeLockType) => Promise<WakeLockSentinelLike>;
}

interface WakeLockNavigatorLike {
    wakeLock?: WakeLockApiLike;
}

interface VisibilityDocumentLike {
    readonly visibilityState: DocumentVisibilityState;
    addEventListener: (type: 'visibilitychange', listener: () => void) => void;
    removeEventListener: (type: 'visibilitychange', listener: () => void) => void;
}

export interface WakeLockControllerDeps {
    document?: VisibilityDocumentLike;
    navigator?: WakeLockNavigatorLike;
    onError?: (err: unknown) => void;
}

export interface WakeLockController {
    setEnabled: (enabled: boolean) => void;
    dispose: () => void;
}

export function createWakeLockController(deps: WakeLockControllerDeps): WakeLockController {
    let enabled = false;
    let disposed = false;
    let sentinel: WakeLockSentinelLike | null = null;
    let releaseListener: (() => void) | null = null;
    let acquirePromise: Promise<void> | null = null;

    const isVisible = () => deps.document?.visibilityState !== 'hidden';

    const detachReleaseListener = () => {
        if (sentinel && releaseListener) {
            sentinel.removeEventListener?.('release', releaseListener);
        }
        releaseListener = null;
    };

    const acquire = () => {
        if (!enabled || disposed || !isVisible() || sentinel || acquirePromise) return;
        const wakeLock = deps.navigator?.wakeLock;
        if (!wakeLock) return;

        acquirePromise = wakeLock.request('screen')
            .then((nextSentinel) => {
                if (!enabled || disposed || !isVisible()) {
                    void nextSentinel.release().catch((err) => deps.onError?.(err));
                    return;
                }

                sentinel = nextSentinel;
                releaseListener = () => {
                    detachReleaseListener();
                    sentinel = null;
                    if (enabled && !disposed && isVisible()) acquire();
                };
                sentinel.addEventListener?.('release', releaseListener);
            })
            .catch((err) => deps.onError?.(err))
            .finally(() => {
                acquirePromise = null;
            });
    };

    const release = () => {
        const active = sentinel;
        detachReleaseListener();
        sentinel = null;
        if (active && !active.released) {
            void active.release().catch((err) => deps.onError?.(err));
        }
    };

    const onVisibilityChange = () => {
        if (isVisible()) acquire();
        else release();
    };

    deps.document?.addEventListener('visibilitychange', onVisibilityChange);

    return {
        setEnabled(nextEnabled: boolean) {
            enabled = nextEnabled;
            if (enabled) acquire();
            else release();
        },
        dispose() {
            disposed = true;
            enabled = false;
            deps.document?.removeEventListener('visibilitychange', onVisibilityChange);
            release();
        },
    };
}

export function useWakeLock(enabled: boolean): void {
    const controllerRef = useRef<WakeLockController | null>(null);
    const errorRef = useRef<unknown>(null);

    useEffect(() => {
        controllerRef.current = createWakeLockController({
            document: typeof document === 'undefined' ? undefined : document,
            navigator: typeof navigator === 'undefined' ? undefined : navigator as WakeLockNavigatorLike,
            onError: (err) => {
                errorRef.current = err;
            },
        });

        return () => {
            controllerRef.current?.dispose();
            controllerRef.current = null;
        };
    }, []);

    useEffect(() => {
        controllerRef.current?.setEnabled(enabled);
    }, [enabled]);
}
